<template>
  <div
    ref="nodeElement"
    class="cf-node"
    :class="[
      `cf-node--${node.nodeTypeId}`,
      node.nodeOptions.size ? `cf-node--${node.nodeOptions.size}` : '',
      isNodeSelected ? 'cf-node--selected' : '',
      hasErrors ? 'error' : '',
      isValidGoToNode ? 'cf-node--available-goto' : '',
    ]"
    :style="nodeStyles"
    @contextmenu.stop.prevent=""
  >
    <div
      v-for="(slot, index) in node.inputs"
      :key="index"
      :class="{ 'cf-node__drop-zone': enableDropzone }"
      @mouseup.stop="endConnection($event, index)"
    >
      <div
        class="cf-node__input"
        :class="{ active: slot.active }"
      >
        <button
          v-if="slot.active"
          class="cf-node__input-remove-link"
          @click="removeConnection"
        >
          <i class="fa fa-times" />
        </button>
      </div>
    </div>

    <div
      class="cf-node__main"
      @mousemove="handleMove"
      @touchmove="handleMove"
      @mousedown="handleDown"
      @touchstart="handleDown"
      @mouseup="handleUp"
      @touchend="handleUp"
    >
      <div class="cf-node__header">
        <div
          v-if="nodeNameTrimmed"
          class="cf-node__display-name"
          :class="[ 
            {'extended': nodeNameLengthProps.extended},
            {'overflow': nodeNameLengthProps.overflow}
          ]"
          :title="nodeNameTrimmed"
        >
          {{ nodeNameTrimmed }}
        </div>
        <i
          v-tooltip.top="node.nodeDescription"
          :class="node.nodeOptions.icon"
          class="cf-node__icon m-r-6"
          :style="nodeIconStyle"
          aria-hidden="true"
        />
        <div class="cf-node__title">
            {{ node.displayName }}
          <img v-if="isVoiceResponseEnabledGatherNode" v-tooltip.top="$t('VOICE_RESPONSE_ENABLED')" class="width-1em m-l-6" src="@/assets/icons/voiceEnabledGatherNodeWhite.svg" alt="" />
          <i
            v-if="hasErrors"
            ref="invalidBadge"
            class="fa fa-warning error m-l-6"
            aria-hidden="true"
            @mousedown.stop=""
            @mouseup.stop="toggleErrors"
          />
          <Popover
            v-if="hasErrors && showErrors"
            v-model="showErrors"
            v-click-outside="{ binding: toggleErrors, focus: '.popover' }"
            placement="right"
            trigger="manual"
            custom-class="cf-node__error-popover"
            :target="popoverTrigger"
          >
            <template slot="popover">
              <ul>
                <template v-if="generalErrors && generalErrors.length">
                  <li
                    v-for="(error, index) in generalErrors"
                    :key="`'gen-error-'${index}`"
                  >
                    {{ error }}
                  </li>
                </template>

                <template v-if="propertyErrors && propertyErrors.length">
                  <li
                    v-for="(error, index) in propertyErrors"
                    :key="`'prop-error-${index}`"
                  >
                    {{ error.propertyName | camelCaseFormat }}
                    {{ error.errorMessage }}.
                  </li>
                </template>
              </ul>
            </template>
          </Popover>
        </div>
        <div
          v-if="!isRootNode"
          class="cf-node__actions"
        >
          <div
            v-if="playUrl"
            class="cf-node__action m-l-4"
            @mousedown.stop=""
            @mouseup.stop=""
          >
            <RecordingButton
              :recording-url="playUrl"
              fixed-player
            />
          </div>
          <button
            class="cf-node__action cf-node__action-delete m-l-8 m-b-2"
            @click.stop.prevent="deleteNode"
          >
            <i
              class="fa fa-trash cf-node__action-delete"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <div
        v-if="node.outputs && node.outputs.length"
        class="cf-node__outputs"
      >
        <div
          v-for="(output, index) in node.outputs"
          :key="index"
          class="cf-node__output"
        >
          {{ output.name }}
          <i
            class="fa fa-square cf-node__output-start"
            @mousedown.stop.prevent="startConnection(index, output, $event)"
          />

          <span
            v-if="showNodeAdd(index)"
            class="cf-node__output-add"
            @click.stop.prevent="showContextMenu($event, { node, index, output })"
          >+</span>
        </div>
      </div>
      <div
        v-if="node.nodeType === 'Unavailable'"
        class="cf-node__message"
      >
        <p v-html="$t('UNAVAILABLE_NODE', { unavailableNodeName })" />
      </div>
    </div>
    <div class="cf-node__select-node">
      <div
        v-if="isValidGoToNode"
        @click="selectNode($event)"
      >
        <i
          class="fa fa-crosshairs"
          aria-hidden="true"
        />
      </div>
      <div v-else />
    </div>
  </div>
</template>

<script>
import messages from '../../locales';
import { getPossibleLinkFilter } from '../../helpers/callFlow';
import { nodeAudioSettingsMap } from '../../helpers/constants';
import RecordingButton from '@/modules/viewAssets/components/RecordingButton';
import { Popover } from 'uiv';
import nodePropsMixin from '../../mixins/nodePropsMixin';
import { mapState, mapGetters } from 'vuex';
import { getNodeStepName } from '../../helpers/node';

export default {
  name: 'Node',
  components: { RecordingButton, Popover },

  mixins: [nodePropsMixin],

  props: {
    /**
     * @type {{ center: { x: number, y: number }, scale: number }}
     */
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    hasChild: {
      type: Boolean,
      default: null
    },
    
    /**
     * @type {Object.<string, import('../../ivr').NodeLinkDetails>}
     */
    nodeLinkDetails: {
      type: Object,
      default() {
        return {};
      }
    },

    enableDropzone: {
      type: Boolean,
      default: null,
    },

    newAdded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      popoverTrigger: null,
      playButton: true,
      showErrors: false,
      audioParentSetting: '',
      audioSettings: {}
    };
  },
  computed: {
    ...mapState({
      featureFlags: 'currentAccountFeatureFlags'
    }),
    ...mapState('callFlows', {
      selectedNode: state => state.scene.selectedNode
    }),

    ...mapGetters('callFlows', [
      'scene'
    ]),

    nodePosition() {
      return this.nodeLinkDetails[this.node.id];
    },

    playUrl() {
      return this.audioSettings &&
        this.audioSettings.isEnabled &&
        this.audioSettings.play?.isEnabled
        ? this.audioSettings.play?.URL
        : null;
    },

    nodeStyles() {
      return {
        top: this.options.center.y + this.nodePosition?.y * this.options.scale + 'px',
        left: this.options.center.x + this.nodePosition?.x * this.options.scale + 'px',
        transform: 'scale(' + (this.options.scale + '') + ')',
        transformOrigin: 'top left',
        '--accent-color': this.nodePosition?.nodeOptions.accentColor,
        '--accent-color-dark': this.lightenColor(this.nodePosition?.nodeOptions.accentColor, -20),
      };
    },
    nodeIconStyle() {
      return {
        color: this.node.nodeOptions.accentColor,
      };
    },
    isRootNode() {
      return this.node.nodeTypeId == 'RootNode';
    },
    isValidGoToNode() {
      return (
        this.node.nodeTypeId != 'RootNode' && this.node.nodeTypeId != 'GoToNode'
      );
    },
    isNodeSelected() {
      return (
        this.selectedNode && 
        this.node.id === this.selectedNode.id && 
        this.selectedNode.selected
      );
    },
    nodeNameTrimmed() {
      return getNodeStepName(this.node);
    },  
    nodeNameLengthProps() {
      return {
        extended: this.nodeNameTrimmed.length > 26,
        overflow: this.nodeNameTrimmed.length > 55
      };
    },
    unavailableNodeName() {
      return this.node.nodeTypeId ? this.node.nodeTypeId : 'Node';
    },
    ivrGatherFeatureFlag() {
      return this.featureFlags.ivr_gather;
    },
    isVoiceResponseEnabledGatherNode() {
      return this.ivrGatherFeatureFlag && this.node.nodeType === 'Gather' && this.node.settings.voiceResponse;
    }
  },
  watch: {
    hasErrors() {
      if (this.hasErrors) {
        this.$nextTick(() => (this.popoverTrigger = this.$refs.invalidBadge));
      }
    },

    node() {
      this.checkAudioSetting();
    }
  },
  created() {
    this.mouseX = 0;
    this.mouseY = 0;
    this.lastMouseX = 0;
    this.lastMouseY = 0;
    this.linking = false;
    this.hasDragged = false;

    this.checkAudioSetting();
  },
  mounted() {
    this.popoverTrigger = this.$refs.invalidBadge;

    if (this.newAdded) {
      /**
       * @type {HTMLDivElement}
       */
      const nodeElement = this.$refs.nodeElement;

      const parentElement = nodeElement.parentElement;

      const parentElementOffsetWidth = parentElement.offsetWidth;
      const parentElementOffsetHeight = parentElement.offsetHeight;

      const centerHeight = parentElementOffsetHeight / 2;
      const centerWidth = parentElementOffsetWidth / 2;

      // Horizontal distance from the parent element (canvas)
      const offsetLeft = nodeElement.offsetLeft;
      // Vertical distance from the parent element (canvas)
      const offsetTop = nodeElement.offsetTop;

      // Calculate how far is the node from the center of the canvas
      const diffX = offsetLeft - centerWidth;
      const diffY = offsetTop - centerHeight;

      let newCenterX = undefined;
      let newCenterY = undefined;


      // Check if the node is not in view
      if (offsetLeft < 0 || offsetLeft > parentElementOffsetWidth) {
        // New center position will be the difference b/w the difference current center position and node's distance from the center of the canvas
        newCenterX = this.options.center.x - diffX;
      }

      if (offsetTop < 0 || offsetTop > parentElementOffsetHeight) {
        // New center position will be the difference b/w the difference current center position and node's distance from the center of the canvas
        newCenterY = this.options.center.y - diffY;
      }

      if (newCenterX || newCenterY) {
        this.$emit('update-scene-container', {
          center: {
            x: newCenterX ? newCenterX - 160 : undefined,
            y: newCenterY ? newCenterY - 50 : undefined
          }
        });
      }

    }
  },
  methods: {
    checkAudioSetting() {
      // Get this node's audio parent setting based on the map in nodeAudioSettingsMap helper const
      this.audioParentSetting = nodeAudioSettingsMap.get(this.node.nodeTypeId);

      this.audioSettings = {
        isEnabled: false,
        play: {
          isEnabled: false
        },
        say: {
          isEnabled: false
        }
      };

      // If the audio component is enabled, set the enabled flags on the object
      if (this.node.settings?.[this.audioParentSetting]?.play?.URL) {
        this.audioSettings.isEnabled = true;
        this.audioSettings.play.isEnabled = true;
        this.audioSettings.play.URL = this.node.settings?.[this.audioParentSetting]?.play?.URL;
      }

      if (this.node.settings?.[this.audioParentSetting]?.say?.text) {
        this.audioSettings.isEnabled = true;
        this.audioSettings.say.isEnabled = true;
        this.audioSettings.say.text = this.node.settings?.[this.audioParentSetting]?.say?.text;
      }
    },
    toggleErrors() {
      this.showErrors = !this.showErrors;
    },
    // Commented out for now, may re-introduce
    // toggleAudio() {
    //   this.playButton = !this.playButton;
    // },
    handleMove() {
      this.hasDragged = true;
    },
    handleDown(e) {
      if (this.showErrors) this.showErrors = !this.showErrors;
      this.hasDragged = false;
      const target = e.target || e.srcElement;

      // Check for touch event to be able to get coordinates
      let ePageX, ePageY, eClientX, eClientY;
      if (e.type.includes('touch')) {
        ePageX = e.touches[0].pageX;
        ePageY = e.touches[0].pageY;
        eClientX = e.touches[0].clientX;
        eClientY = e.touches[0].clientY;
      } else {
        ePageX = e.pageX;
        ePageY = e.pageY;
        eClientX = e.clientX;
        eClientY = e.clientY;
      }

      if (target != this.$refs.editIcon) {
        this.mouseX = ePageX || eClientX + document.documentElement.scrollLeft;
        this.mouseY = ePageY || eClientY + document.documentElement.scrollTop;

        this.lastMouseX = this.mouseX;
        this.lastMouseY = this.mouseY;

        const lastCoords = {
          x: this.lastMouseX,
          y: this.lastMouseY,
        };
        // Emit node and coordinates to parent for tracking movement
        this.$emit('drag', { node: this.node, lastCoords: lastCoords });

        e.preventDefault();
      }
    },
    handleUp(e) {
      if (e.target.classList.contains('cf-node__output-add')) {
        return;
      }
      if (e.target.classList.contains('cf-node__action-delete')) {
        return;
      }
      if (!this.hasDragged && !this.isRootNode) {
        this.selectNode();
      }

      if (this.linking) {
        this.linking = false;
      }
    },
    selectNode() {
      this.$emit('selectNode', this.node);
    },
    deleteNode() {
      this.$emit('deleteNode', this.node);
    },
    startConnection(index, output, e) {
      // TODO: Remove isOnFailure and just use onSuccess;
      const isOnFailure = !output.onSuccess;
      this.$emit('startConnection', {
        slotNumber: index,
        isOnFailure,
        criteria: output.minimumCriteria,
        e,
      });
    },
    endConnection(e, index) {
      this.$emit('endConnection', index);
    },
    removeConnection() {
      this.$emit('removeConnection', this.node.id);
    },
    showContextMenu(e, { node, index, output }) {
      const isOnFailure = !output.onSuccess;

      let possibleFilterValue = undefined;

      if (this.node.nodeTypeId === 'MenuNode' || this.node.nodeTypeId === 'SplitTestNode') {
        possibleFilterValue = getPossibleLinkFilter(this.node, this.scene);

        if (this.nodePosition.outputs[index]?.active && index !== 1 && possibleFilterValue == null) {
          return;
        }
      }

      possibleFilterValue = this.node.nodeTypeId === 'SplitTestNode' ? 'Route: ' + possibleFilterValue : possibleFilterValue;

      this.$emit('showContextMenu', e, {
        node,
        slotNumber: index,
        isOnFailure,
        filter: !isOnFailure ? possibleFilterValue : undefined,
        criteria: output.minimumCriteria,
      });
    },
    lightenColor(color, percent) {
      if (!color) return;
      const num = parseInt(color.replace('#', ''), 16),
        amt = Math.round(2.55 * percent),
        R = (num >> 16) + amt,
        B = ((num >> 8) & 0x00ff) + amt,
        G = (num & 0x0000ff) + amt;
      return (
        '#' +
        (
          0x1000000 +
          (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
          (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
          (G < 255 ? (G < 1 ? 0 : G) : 255)
        )
          .toString(16)
          .slice(1)
      );
    },

    showNodeAdd(outputIndex) {
      if (this.node.nodeTypeId === 'RouterNode') {
        return true;
      }

      if (this.node.nodeTypeId === 'MenuNode' || this.node.nodeTypeId === 'SplitTestNode') {
        const possibleFilterValue = getPossibleLinkFilter(this.node, this.scene);

        if (this.nodePosition?.outputs[outputIndex]?.active && outputIndex !== 1 && possibleFilterValue != null) {
          return true;
        }
      }
      return !this.nodePosition?.outputs[outputIndex]?.active;
    }
  },
  i18n: {
    messages,
    locale: 'en'
  }
};
</script>
<style lang="scss" scoped>
.cf-node {
  &--selected {
    box-shadow: 0px 0px 6px 1px var(--accent-color);
  }

  &__input {
    background: var(--accent-color);
    background-image: linear-gradient(
      to bottom right,
      var(--accent-color) 30%,
      var(--accent-color-dark) 100%
    );
  }

  &__display-name {
    color: var(--accent-color);
  }
  &__drop-zone {
    margin: -40px -150px -40px -40px;
    padding: 40px 150px 40px 40px;
    z-index: 3;
    cursor: crosshair;
  }
}
</style>